import * as THREE from 'three';
import type { Surface } from '../../core/hp/types';
import type { MaterialTarget } from '../../state/store';
import { surfaceRepeat } from '../../core/hp/surfaceTransform';
import { subscribeAssets } from './assetEvents';
import { hasPicture } from './TextureImageCache';

export interface MaterialSlotInfo {
  key: string; label: string; surface: Surface; hasTexture: boolean; weight: number;
  previewUrl?: string; textureName?: string;
}

export function bindMaterialSurface(material: THREE.Material, surface: Surface | undefined, explicitTiling: boolean,
  wallSide?: 'front' | 'back'): void {
  material.userData.hpSurface = surface ? structuredClone(surface) : undefined;
  material.userData.hpExplicitTiling = explicitTiling;
  if (wallSide) material.userData.hpWallSide = wallSide;
}

export function describeMaterial(material: THREE.MeshStandardMaterial): Pick<MaterialSlotInfo, 'surface' | 'hasTexture' | 'previewUrl' | 'textureName'> {
  const stored = material.userData.hpSurface as Surface | undefined, map = material.map;
  const color = material.color.clone().convertLinearToSRGB();
  const fallback: Surface = { shininess: 1 - material.roughness,
    ...(map ? { tileMeters: [1 / Math.max(Math.abs(map.repeat.x), .0001), 1 / Math.max(Math.abs(map.repeat.y), .0001)] as [number, number],
      offset: map.offset.toArray(), rotation: -map.rotation * 180 / Math.PI }
      : { color: color.toArray() as [number, number, number] }),
    ...(material.opacity < 1 ? { opacity: material.opacity } : {}),
  };
  return { surface: stored ? { ...fallback, ...structuredClone(stored) } : fallback, hasTexture: !!map,
    previewUrl: map ? previewImage(map) : undefined,
    textureName: stored?.texture ?? stored?.customTexture ?? (map?.name || undefined) };
}

const embeddedPreviews = new WeakMap<THREE.Texture['source'], string>();
function previewImage(texture: THREE.Texture): string | undefined {
  if (typeof texture.userData.imageUrl === 'string') return texture.userData.imageUrl;
  const image = texture.image as { currentSrc?: string; src?: string; width?: number; height?: number } | undefined;
  const url = image?.currentSrc || image?.src;
  if (url && !url.startsWith('blob:')) return url;
  if (typeof document === 'undefined') return url;
  const cached = embeddedPreviews.get(texture.source);
  if (cached) return cached;
  // GLTFLoader revokes embedded blob URLs after decoding; ImageBitmap has no URL.
  // A view-only preview keeps the source finish sharp on high-density displays.
  if (!image?.width || !image.height) return undefined;
  try {
    const canvas = document.createElement('canvas');
    const factor = Math.min(1, 512 / Math.max(image.width, image.height));
    canvas.width = Math.max(1, Math.round(image.width * factor)); canvas.height = Math.max(1, Math.round(image.height * factor));
    canvas.getContext('2d')!.drawImage(image as CanvasImageSource, 0, 0, canvas.width, canvas.height);
    const preview = canvas.toDataURL('image/png'); embeddedPreviews.set(texture.source, preview);
    return preview;
  } catch { return undefined; }
}

interface MaterialAssignment { mesh: THREE.Mesh; original: THREE.Material | THREE.Material[] }
interface Sampler { texture: THREE.Texture; original: THREE.Texture; explicit: boolean }

/** Native MaterialPreviewSession: presentation-only UV updates, followed by one document
 * commit. Geometry, source materials, decoded images and unrelated instances stay intact. */
export class MaterialTransformPreview {
  private assignments: MaterialAssignment[] = [];
  private materials = new Set<THREE.Material>();
  private samplers: Sampler[] = [];
  private baseline: Surface = {};
  private rootAtBegin?: THREE.Object3D;
  private unsubscribe?: () => void;
  private releaseBorrow?: () => void;
  get active(): boolean { return this.assignments.length > 0; }

  constructor(private root: () => THREE.Object3D | undefined, private retain?: (root: THREE.Object3D) => () => void) {}

  begin(target: NonNullable<MaterialTarget>, baseline: Surface): boolean {
    this.end();
    const root = this.root();
    if (!root) return false;
    const name = target.kind === 'item' ? `item:${target.itemId}` : target.kind === 'opening' ? `opening:${target.openingId}` : target.kind === 'wall' ? `wall:${target.wallId}`
      : target.kind === 'trim' ? `${target.trim}:${target.wallId}` : `${target.kind}:${target.roomIndex}`;
    const node = root.getObjectByName(name);
    if (!node) return false;
    this.rootAtBegin = root; this.baseline = structuredClone(baseline);
    const materialCopies = new Map<THREE.Material, THREE.Material>();
    node.traverse(object => {
      if (!(object instanceof THREE.Mesh) || object.userData.selectionPresentation) return;
      const originals = Array.isArray(object.material) ? object.material : [object.material];
      let changed = false;
      const replacements = originals.map(material => {
        if (!(material instanceof THREE.MeshStandardMaterial) || !material.map) return material;
        if (target.kind === 'wall' && material.userData.hpWallSide !== target.side) return material;
        if ((target.kind === 'item' || target.kind === 'opening') && target.slot !== '*' && normalized(material.name) !== normalized(target.slot)) return material;
        changed = true;
        let copy = materialCopies.get(material);
        if (copy) return copy;
        copy = material.clone(); materialCopies.set(material, copy); this.materials.add(copy);
        const textures = new Map<THREE.Texture, THREE.Texture>();
        for (const [key, value] of Object.entries(material)) {
          if (!(value instanceof THREE.Texture) || value.mapping !== THREE.UVMapping) continue;
          let texture = textures.get(value);
          if (!texture) {
            texture = value.clone(); textures.set(value, texture);
            // Ready KTX replacement keeps the original sampler identity. Its
            // generic Texture clone must retain the compressed upload path too.
            if ('isCompressedTexture' in value) Object.assign(texture, { isCompressedTexture: true });
            this.samplers.push({ texture, original: value, explicit: !!material.userData.hpExplicitTiling });
          }
          (copy as unknown as Record<string, unknown>)[key] = texture;
        }
        return copy;
      });
      if (changed) {
        this.assignments.push({ mesh: object, original: object.material });
        object.material = Array.isArray(object.material) ? replacements : replacements[0];
      }
    });
    if (this.active) this.releaseBorrow = this.retain?.(node);
    // A copy of a sampler whose picture is on its way draws the cache's stand-in; when the picture
    // lands, the copy follows its original to the picture's Source, releasing the pixel's upload first.
    const waiting = new Set(this.samplers.filter(sampler => !hasPicture(sampler.texture)));
    if (waiting.size) this.unsubscribe = subscribeAssets(() => {
      for (const sampler of waiting) if (hasPicture(sampler.original)) {
        if (sampler.texture.source !== sampler.original.source) { sampler.texture.dispose(); sampler.texture.source = sampler.original.source; }
        sampler.texture.needsUpdate = true; waiting.delete(sampler);
      }
    });
    return this.active;
  }

  update(draft: Surface): void {
    if (!this.active) return;
    if (this.root() !== this.rootAtBegin) { this.end(); return; }
    for (const sampler of this.samplers) {
      const before = surfaceRepeat(this.baseline, sampler.explicit), after = surfaceRepeat(draft, sampler.explicit);
      sampler.texture.repeat.set(sampler.original.repeat.x * after[0] / before[0], sampler.original.repeat.y * after[1] / before[1]);
      sampler.texture.rotation = sampler.original.rotation - ((draft.rotation ?? 0) - (this.baseline.rotation ?? 0)) * Math.PI / 180;
      sampler.texture.offset.set(sampler.original.offset.x + (draft.offset?.[0] ?? 0) - (this.baseline.offset?.[0] ?? 0),
        sampler.original.offset.y + (draft.offset?.[1] ?? 0) - (this.baseline.offset?.[1] ?? 0));
      sampler.texture.updateMatrix(); // Uniform update only; no texture upload / needsUpdate.
    }
  }

  end(): void {
    for (const assignment of this.assignments) assignment.mesh.material = assignment.original;
    for (const material of this.materials) material.dispose();
    for (const sampler of this.samplers) sampler.texture.dispose();
    this.releaseBorrow?.(); this.releaseBorrow = undefined;
    this.unsubscribe?.(); this.unsubscribe = undefined;
    this.assignments = []; this.materials.clear(); this.samplers = []; this.rootAtBegin = undefined;
  }
}

function normalized(name: string): string { return name.replace(/\s*\(Instance\)\s*$/i, '').trim().toLowerCase(); }
